<template>
  <section v-if="interests?.length" class="interests pe-section">
    <h3>
      Interests
      <SectionActions
        v-show="isPowerEdit"
        class="actions"
        section="interests"
        :actions="['delete', 'add']"
      />
    </h3>
    <section class="list">
      <template v-for="(interest, index) in interests" :key="interest.name">
        <span
          v-if="interest.name"
          :data-index="index"
          :data-name="interest?.name"
          @keydown.enter.stop.prevent="e => addInterest(e, index)"
          @keydown.delete.stop="e => removeInterest(e, index)"
          class="interest"
        >
          <span
            v-editable="isPowerEdit"
            :data-target-path="`resume.interests.${index}.name`"
            v-text="interest.name"
          ></span>
          <ItemActions
            v-show="isPowerEdit"
            section="interests"
            :index="index"
            :uuid="interest.uuid"
            :actions="['delete']"
          />
        </span>
      </template>
    </section>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType, toRefs, nextTick } from 'vue'
import { Resume } from '@/types/resume/v1'
import SectionActions from './SectionActions.vue'
import ItemActions from './ItemActions.vue'
import { useStructuredResumeStore } from '@/store/pinia/structuredResume'

export default defineComponent({
  name: 'Interests',
  components: {
    SectionActions,
    ItemActions,
  },
  props: {
    interests: {
      type: Array as PropType<Resume.Interest[]>,
      required: false,
      default: () => [],
    },
  },
  setup(props) {
    const store = useStructuredResumeStore()
    const { interests } = toRefs(props)
    const addInterest = async (event, index) => {
      interests.value.splice(index + 1, 0, {
        name: 'New interest...',
        keywords: [],
      })
      await nextTick()
      const nextItem = event.currentTarget?.parentElement
        ?.querySelector(`span.interest[data-index="${index + 1}"]`)
        ?.querySelector('span')
      focusElement(nextItem)
    }

    const removeInterest = async (event, index) => {
      if (
        event.currentTarget?.querySelector('span')?.innerText.trim().length > 0
      ) {
        return
      }
      event.preventDefault()
      const previousItem = event.currentTarget?.parentElement
        ?.querySelector(`span.interest[data-index="${index - 1}"]`)
        ?.querySelector('span')
      setCursor(previousItem)
      store.cleanInterests()
    }

    const setCursor = element => {
      if (!element) return
      element.focus()
      const range = document.createRange()
      const selection = window.getSelection()
      range.setStart(element, element.childNodes.length)
      range.setEnd(element, element.childNodes.length)
      range.collapse(true)
      selection?.removeAllRanges()
      selection?.addRange(range)
    }

    const focusElement = element => {
      element.focus()
      const selection = window.getSelection()
      selection?.removeAllRanges()
      const range = document.createRange()
      range.selectNodeContents(element)
      selection?.addRange(range)
    }

    return {
      isPowerEdit: store.isPowerEdit,
      addInterest,
      removeInterest,
    }
  },
})
</script>

<style lang="postcss" scoped>
.interests {
  @apply text-base-em leading-[inherit] pt-6-em @container;
  h3 {
    @apply leading-none text-xl-em flex items-center;
    .actions {
      @apply hidden;
    }
    &:hover {
      .actions {
        @apply flex;
      }
    }
  }
  .list {
    @apply pt-4-em;
    .interest {
      @apply text-sm-em inline relative min-w-[6px] h-auto;
      &:not(:last-child) {
        &::after {
          content: ', ';
        }
      }
      .itemActions {
        @apply hidden;
      }
      &:hover {
        .itemActions {
          @apply flex;
        }
      }
    }
  }
}
</style>
